<template>
  <div class="page2">
    <div class="feature-intro title flex justify-content-left">
      <h1 class="pl-0 pt-2">Efectos Cargados</h1>
    </div>
    <div>
      <TemplateTable
        tableTitle="Deposito Judicial"
        reference="deposito/efectos"
        :loading="loading"
        v-on:selectItem="$emit('selectItem', $event)"
        :idTable="idTable"
        :url="url"
        :data="data"
        :modal="modal"
        :titles="titles"
        order="exp_pre_EfectoDeposito.FechaCreacion desc"
        apiversion="2"
        :getLimit="10"
        :formatDoc="formatDoc"
        :styles="styles"
        :resizableColumns="true"
        columnResizeMode="expand"
        :keyShow="keyShow"
        :filtersFields="filtersFields"
        :multiSelect="true"
        :enableSelectAllCheckbox="false"
        :enableDeleteButton="enableDeleteButton"
        :enableViewButtons="true"
        :enableGlobalSearch="false"
        :enableAddButton="{ enabled: enableAddButton, name: 'newEfecto', params: { id: 'add' } }"
        :enableUploadFile="false"
        :enableFilterMultiple="true"
        :enableRefresh="true"
        :dropdown="dropdown"
        v-on:datosFiltros="onResultados"
        emptyMsg="No existen registros para mostrar"
        :classesDivContainer="classesDivContainer"
        :removeLimit="true"
        ref="templateTableRef"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useStoreCompat } from "@/composables/useStoreCompat";
import { useUserAccess } from "@/composables/useUserAccess";
import { useDate } from "@/composables/useDate";

defineProps({
  modal: {
    type: Boolean,
  },
});

defineEmits(["selectItem"]);

const store = useStoreCompat();
const { esTipoUsuario } = useUserAccess();
const { date2Es } = useDate();

const user = ref(store.state.user);
const templateTableRef = ref(null);

const loading = ref(true);
const data = ref(null);
const url = `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/deposito`;
const idTable = "EfectoDepositoID";

const enableDeleteButton = ref(null);
const enableAddButton = ref(null);

const filtersFields = ref([
  { name: "N° Motor", field: "NumeroMotor" },
  { name: "N° Chasis", field: "Chasis" },
  { name: "Imei", field: "Imei" },
  { name: "Dominio", field: "Dominio" },
  { name: "Año", field: "Anio" },
  { name: "Categoría", field: "CategoriaID" },
  { name: "Subcategoría", field: "SubcategoriaID" },
  { name: "Fecha de Creacion", field: "exp_pre_EfectoDeposito.FechaCreacion", type: "date" },
  { name: "Marcas", field: "MarcaID" },
  { name: "Modelos", field: "ModeloID" },
  { name: "Visible", field: "Visible", type: "dropdown" },
  { name: "Estado", field: "exp_pre_EstadoEfecto.TipoEstadoEfectoID" },
  { name: "Depositario Judicial", field: "exp_pre_Usuario.UsuarioID" },
]);

const dropdown = ref({
  "exp_pre_Usuario.UsuarioID": {
    optionValue: "UsuarioID",
    optionLabel: "apellido_nombre",
    options: [],
    refreshDropdown: () => {
      store.dispatch("siis/setUserList", "efectos", true);
    },
    loadDropdown: async () => {
      await store.dispatch("siis/setUserList", "efectos", true);
      dropdown.value["exp_pre_Usuario.UsuarioID"].options = store.state.siis.userList;
    },
  },
  "exp_pre_EstadoEfecto.TipoEstadoEfectoID": {
    optionValue: "TipoEstadoEfectoID",
    optionLabel: "Nombre",
    options: [],
    refreshDropdown: () => {
      store.dispatch("siis/setEstadosEfectoDeposito", true);
    },
    loadDropdown: async () => {
      await store.dispatch("siis/setEstadosEfectoDeposito");
      dropdown.value["exp_pre_EstadoEfecto.TipoEstadoEfectoID"].options =
        store.state.siis.estadosEfectoDeposito;
    },
  },
  ModeloID: {
    optionValue: "ModeloID",
    optionLabel: "Nombre",
    options: [],
    refreshDropdown: () => {
      store.dispatch("siis/setModelo", true);
    },
    loadDropdown: async () => {
      await store.dispatch("siis/setModelo");
      dropdown.value.ModeloID.options = store.state.siis.modelo;
    },
  },
  MarcaID: {
    optionValue: "MarcaID",
    optionLabel: "nombre",
    options: [],
    refreshDropdown: () => {
      store.dispatch("siis/setMarca", true);
    },
    loadDropdown: async () => {
      await store.dispatch("siis/setMarca");
      dropdown.value.MarcaID.options = store.state.siis.marca;
    },
    changeDropdown: async (v) => {
      await store.dispatch("siis/setModelo", true);
      dropdown.value.ModeloID.options = store.state.siis.modelo.filter(
        (item) => item.MarcaID === v.MarcaID
      );
    },
  },
  CategoriaID: {
    optionValue: "CategoriaID",
    optionLabel: "Nombre",
    options: [],
    refreshDropdown: () => {
      store.dispatch("siis/setCategoriasDeposito", true);
    },
    loadDropdown: async () => {
      await store.dispatch("siis/setCategoriasDeposito");
      dropdown.value.CategoriaID.options = store.state.siis.categoriasDeposito;
    },
  },
  SubcategoriaID: {
    optionValue: "SubcategoriaID",
    optionLabel: "Nombre",
    options: [],
    refreshDropdown: () => {
      store.dispatch("siis/setSubCategoriasDeposito", true);
    },
    loadDropdown: async () => {
      await store.dispatch("siis/setSubCategoriasDeposito");
      dropdown.value.SubcategoriaID.options = store.state.siis.subcategoriasDeposito;
    },
  },
  DependenciaID: {
    optionValue: "id",
    optionLabel: "dependencia",
    options: [],
    refreshDropdown: () => {
      store.dispatch("siis/setDependencias", true);
    },
    loadDropdown: async () => {
      await store.dispatch("siis/setDependencias");
      dropdown.value.DependenciaID.options = store.state.siis.dependencia;
    },
  },
  Visible: {
    optionValue: "value",
    optionLabel: "name",
    options: [
      { value: 1, name: "Si" },
      { value: 0, name: "No" },
    ],
  },
  "exp_pre_VinculoActuacionDeposito.TipoActuacion": {
    optionValue: "value",
    optionLabel: "name",
    options: [],
  },
});

const keyShow = ref([
  "FechaCreacion",
  "categorias",
  "subcategoria",
  "marcas",
  "modelos",
  "tipo",
  "TipoActuacion",
  "Estado",
]);

const titles = ref({
  FechaCreacion: "Creación",
  categorias: "Categoría",
  subcategoria: "Sub-Categoría",
  marcas: "Marca",
  modelos: "Modelo",
  tipo: "Tipo",
  TipoActuacion: "Tipo Actuación",
  Estado: "Estado",
});

const formatDoc = (col, row) => {
  switch (col) {
    case "FechaCreacion":
      return date2Es(row[col], true);
    case "CategoriaID":
      return store.state.siis.categoriasArticulosObj[row[col]].name;
    case "SubcategoriaID":
      return store.state.siis.categoriasArticulosObj[row[col]].path;
    case "TipoActuacion":
    case "NumeroInfraccion":
      if (
        row.TipoActuacion === null &&
        row.NumeroInfraccion === null &&
        row.TiposumarioID === null
      ) {
        return "Objeto no vinculado";
      }
      if (row.TiposumarioID !== null) {
        return row.TiposumarioID;
      }
      return row.NumeroInfraccion ? "Infraccion" : row.TipoActuacion;
    default:
      return row[col];
  }
};

const styles = {
  default: {
    header: { width: "320px" },
    body: { width: "200px" },
  },
  jerarquia: {
    header: { width: "200px" },
    body: { width: "200px" },
  },
};

const classesDivContainer = {
  Estado(value) {
    switch (value) {
      case "En Dependencia":
        return "eSuccess uppercase";
      case "Depósito Cavic":
      case "Depósito 9 de Julio":
        return "eWarning uppercase";
      case "A Justicia":
        return "eAlert uppercase";
      case "Orden de destrucción":
        return "eDanger uppercase";
      case "En Peritaje":
        return "eInfo uppercase";
      case "Entrega al propietario":
        return "ePrimary uppercase";
      case "Precarga":
        return "eNew uppercase";
      default:
        return "";
    }
  },
};

const viewData = computed(() => store.state.sumarioViews);

const vinculoActuacionDepositoOptions = computed(() => {
  let options = [];
  let filterName = "";
  for (const actuationName in viewData.value) {
    filterName = Object.keys(viewData.value[actuationName]?.filters || {})[0];
    options.push({
      name: filterName,
      value: viewData.value[actuationName]?.filters?.[filterName]?.deposito?.value,
    });
  }
  options = options.filter((obj) => !Object.values(obj).some((value) => value === undefined));
  options.push({
    name: "Infracción",
    value: "NULL",
    injectString:
      '"exp_pre_VinculoInfraccionDeposito.VinculoInfraccionDepositoID":[{"operator":"IS NOT NULL","value":""}]',
    singleFilter: true,
  });
  return options;
});

const onResultados = (mostrar) => {
  // eslint-disable-next-line no-unused-vars
  const datos = mostrar;
};

// eslint-disable-next-line no-unused-vars
const downloadXls = () => {
  window.location.assign(`${url}/d1_indice?getXLS`);
};

const initDropdownsAndFilters = () => {
  filtersFields.value.push({
    name: "Actuaciones",
    field: "exp_pre_VinculoActuacionDeposito.TipoActuacion",
    type: "multiple",
    multiple: true,
  });
  dropdown.value["exp_pre_Usuario.UsuarioID"].loadDropdown();
  dropdown.value["exp_pre_EstadoEfecto.TipoEstadoEfectoID"].loadDropdown();
  dropdown.value.MarcaID.loadDropdown();
  dropdown.value.ModeloID.loadDropdown();
  dropdown.value.CategoriaID.loadDropdown();
  dropdown.value.SubcategoriaID.loadDropdown();
  if (esTipoUsuario(["Super User", "User Tester", "Super Usuario de E3"])) {
    filtersFields.value.push({
      name: "Dependencias",
      field: "DependenciaID",
      type: "multiple",
      multiple: true,
    });
    dropdown.value.DependenciaID.loadDropdown();
    keyShow.value.splice(keyShow.value.length - 1, 0, "dependencia");
    titles.value.dependencia = "Ubicación";
  }
  filtersFields.value.sort((a, b) => a.name.localeCompare(b.name));
  dropdown.value["exp_pre_VinculoActuacionDeposito.TipoActuacion"].options =
    vinculoActuacionDepositoOptions.value;
};

initDropdownsAndFilters();

onMounted(() => {
  enableAddButton.value = user.value.userdata.tipoUsuario !== "UsuarioDepartamental";

  watch(
    () => templateTableRef.value?._data?.selectedCards,
    (newValue) => {
      enableDeleteButton.value = esTipoUsuario([
        "Super User",
        "User Tester",
        "Super Usuario de E3",
      ]);
      const hasActuations = (newValue || []).some((effect) => effect.Origen === "Actuacion");
      if (hasActuations) {
        enableDeleteButton.value = false;
      }
    }
  );
});
</script>

<style scoped>
.title {
  margin-left: 20px;
}
</style>
